import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Button,
  DataTable,
  Tabs,
  SkeletonPage,
  SkeletonBodyText,
} from '@shopify/polaris';
import { IdentifierCoverageCard } from '../components/IdentifierCoverageCard';
import { useIdentifierReport, useIdentifierProducts } from '../hooks/useIdentifierReport';
import { PageEntrance } from '../components/ui';
import './IdentifierReportPage.css';

/**
 * T076: IdentifierReportPage.
 * IdentifierCoverageCard, per-product drilldown table, CSV export,
 * 100% coverage success state.
 */

const STATUS_TABS = [
  { id: 'MISSING', content: 'Missing', panelID: 'missing-panel' },
  { id: 'INVALID', content: 'Invalid', panelID: 'invalid-panel' },
  { id: 'VALID', content: 'Valid', panelID: 'valid-panel' },
];

export function IdentifierReportPage(): React.ReactElement {
  const navigate = useNavigate();
  const { data: report, isLoading, error } = useIdentifierReport();
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(1);

  const status = STATUS_TABS[selectedTab].id as 'VALID' | 'INVALID' | 'MISSING';
  const { data: products } = useIdentifierProducts(status, page);

  if (isLoading) {
    return (
      <SkeletonPage title="Identifier Report">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (error || !report) {
    return (
      <Page title="Identifier Report" backAction={{ content: 'Dashboard', onAction: () => navigate('/dashboard') }}>
        <Banner tone="critical">
          <p>{error?.message ?? 'Unable to load identifier report.'}</p>
        </Banner>
      </Page>
    );
  }

  const isPerfect = report.coveragePercent === 100 && report.invalidGtins === 0;

  const rows = (products?.data ?? []).map((p) => [
    p.title,
    p.shopifyProductId,
    p.barcode ?? '—',
    <Badge
      key={p.id}
      tone={
        p.barcodeStatus === 'VALID' ? 'success' : p.barcodeStatus === 'INVALID' ? 'critical' : 'warning'
      }
    >
      {p.barcodeStatus}
    </Badge>,
    p.hasSku ? '✓' : '✗',
    p.hasMpn ? '✓' : '✗',
  ]);

  return (
    <PageEntrance>
    <Page
      title="Identifier Report"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/dashboard') }}
      primaryAction={{
        content: 'Export CSV',
        url: '/api/identifiers/export',
        external: true,
      }}
    >
      <BlockStack gap="400">
        {/* Hero CTA */}
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">🏷️ Barcode & identifier coverage at a glance</h2>
            <p className="z-hero-subtitle">
              Ensure every product has valid GTINs, SKUs, and MPNs for maximum visibility on Google Shopping and AI marketplaces.
            </p>
          </div>
        </div>
      <Layout>
        {/* Success Banner */}
        {isPerfect && (
          <Layout.Section>
            <Banner title="100% Coverage!" tone="success">
              <p>
                All products have valid identifiers. Your products are fully optimised
                for Google Shopping and other marketplaces.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Coverage Card */}
        <Layout.Section variant="oneThird">
          <IdentifierCoverageCard
            totalProducts={report.totalProducts}
            validGtins={report.validGtins}
            invalidGtins={report.invalidGtins}
            missingGtins={report.missingGtins}
            withSku={report.withSku}
            withMpn={report.withMpn}
            coveragePercent={report.coveragePercent}
          />
        </Layout.Section>

        {/* Per-Product Drilldown */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Products by Status
              </Text>

              <Tabs
                tabs={STATUS_TABS}
                selected={selectedTab}
                onSelect={(idx) => {
                  setSelectedTab(idx);
                  setPage(1);
                }}
              >
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Title', 'Product ID', 'Barcode', 'Status', 'SKU', 'MPN']}
                  rows={rows}
                  footerContent={
                    products?.meta
                      ? `Page ${products.meta.page} of ${products.meta.totalPages} (${products.meta.total} total)`
                      : undefined
                  }
                />
              </Tabs>

              {/* Pagination */}
              {products?.meta && products.meta.totalPages > 1 && (
                <InlineStack gap="200" align="center">
                  <Button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    size="slim"
                  >
                    Previous
                  </Button>
                  <Text as="span" variant="bodySm">
                    Page {page} of {products.meta.totalPages}
                  </Text>
                  <Button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= products.meta.totalPages}
                    size="slim"
                  >
                    Next
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}
